import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Linkedin, Facebook, Twitter } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/Section";
import { ARTICLES, COMPANY } from "@/data/site";
import NotFound from "@/pages/NotFound";

export default function NewsArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  const articleSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      datePublished: article.dateIso,
      author: { "@type": "Organization", name: COMPANY.name },
      publisher: { "@type": "Organization", name: COMPANY.name },
      image: window.location.origin + article.image,
      articleSection: article.category,
      keywords: article.tags.join(", "),
    };
  }, [article]);

  if (!article) return <NotFound />;

  const shareUrl = window.location.href;
  const shareLinks = [
    {
      label: "Share on LinkedIn",
      testid: "article-share-linkedin",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      label: "Share on Facebook",
      testid: "article-share-facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      label: "Share on X",
      testid: "article-share-x",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`,
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} — BNM Sustainable Energy Limited`}
        description={article.excerpt}
        image={article.image}
        type="article"
        jsonLd={articleSchema}
      />

      <section data-testid="article-hero" className="relative overflow-hidden bg-obsidian grid-lines-dark noise-overlay pt-32 sm:pt-40 pb-14 sm:pb-20">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal y={14}>
            <Link
              to="/news"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-volt/80 hover:text-volt transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              News & Insights
            </Link>
            <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.18em]">
              <span className="text-volt">{article.category}</span>
              <span className="text-slate-500"> · {article.date} · {article.author}</span>
            </p>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.12] text-white text-balance">
              {article.title}
            </h1>
          </Reveal>
        </div>
      </section>

      <section data-testid="article-body" className="bg-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Reveal>
            <figure className="rounded-lg border border-slate-200 overflow-hidden bg-white">
              <img src={article.image} alt={article.imageAlt} className="w-full h-auto" width="1024" height="768" />
              <figcaption className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Official BNM photography
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-6">
              {article.body.map((para, i) => (
                <p key={i} className={`leading-relaxed text-slate-700 ${i === 0 ? "text-lg sm:text-xl font-medium text-slate-900" : "text-base"}`}>
                  {para}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-slate-300 bg-white px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-600">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Share this story</p>
              <div className="flex items-center gap-3">
                {shareLinks.map((s) => (
                  <a
                    key={s.testid}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-400 transition-colors"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Questions about this story?"
        text="For media, partnership or technical inquiries related to this announcement, contact the BNM team."
        primary={{ label: "Contact BNM", to: "/contact", testid: "article-contact-cta" }}
        secondary={{ label: "More News", to: "/news", testid: "article-more-news-cta" }}
        testid="article-cta"
      />
    </>
  );
}
